import React from 'react';
import { Factory, Utensils, BookOpen, HeartPulse, GraduationCap, Building, ArrowRight } from 'lucide-react';
import { businesses } from '../mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const iconMap = {
  'factory': Factory,
  'utensils': Utensils,
  'book-open': BookOpen,
  'heart-pulse': HeartPulse,
  'graduation-cap': GraduationCap,
  'building': Building
};

export const Businesses = () => {
  return (
    <section id="businesses" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
            Our Ventures
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Business for Social Good
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-600 to-teal-600 mx-auto rounded-full mb-6"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            Our diverse portfolio of enterprises generates sustainable revenue that directly funds 
            community welfare programs, creating a self-sustaining model of social impact.
          </p>
        </div>

        {/* Businesses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {businesses.map((business, index) => {
            const IconComponent = iconMap[business.icon] || Building;
            
            return (
              <Card 
                key={business.id}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden cursor-pointer"
              >
                <CardContent className="p-0">
                  {/* Icon Header */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-200/30 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <IconComponent className="w-8 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {business.name}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {business.description}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        <span className="text-sm font-semibold text-emerald-700">
                          {business.impact}
                        </span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-10 text-center text-white shadow-xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Business Excellence Meets Social Purpose
          </h3>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
            Every rupee earned through our businesses is reinvested into community welfare programs, 
            creating a sustainable cycle of growth and impact.
          </p>
          <Button 
            size="lg"
            className="bg-white text-emerald-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Partner With Us
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};
